import React, { useEffect, useMemo, useState } from "react";
import ComponentAttributeToolbar from "../AttributeTable/ComponentAttributeToolbar";
import { useSelectedComponent } from "../../../Hooks/Display/useComponents";
import { useAttributes, useFetchedValues, useAttributeFormBuilder, useChangeBooleans, useComponentProperties } from "../../../Hooks/Display/useComponentProperties";
import { getAllAttributes } from "../../Helpers/Schema/Schema";
import { usePropsStoreValue } from "../../../Hooks/PropStore/usePropsStore";
import { filterAttributesForComponent } from "../AttributeTable/utils";
import SyncAttributeDialog from "../AttributeTable/SyncAttributeDialog/SyncAttributeDialog";
import { bySystemAttributeDesc } from "../utils";
import Skeleton from "../../Shared/Skeleton/Skeleton";
import { useSelectedPage } from "../../../Hooks/Display/usePages";
import Toolbar from "./Toolbar";
import Scrollable from "../../Shared/Edit/Scrollable";
import { getAttributesFromLayout, isFormActionsChanged, isStepsChanged } from "./utils";
import { getEntityPages, getReferenceAttributeEntityPages } from "../../../API/Display/Display";
import Steps from "./Steppers/Steps";
import StepLayout from "./Steppers/StepLayout";
import FormActions from "./Steppers/FormActions";
import CreateSection from "./Sections/CreateSection";
import { uuidv4 } from "../../../Hooks/Display/utils";
import { getAttributes } from "../../../API/Attribute/Attribute";

const AttributeFormBuilder = () => {

    const selectedPage = useSelectedPage();
    const selectedComponent = useSelectedComponent();
    const [properties, setProperty] = useComponentProperties();
    const [state, dispatch] = useAttributes();
    const [{ steps, selectedStepId, formActions, formBuilderLoading }, dispatchLayout] = useAttributeFormBuilder();
    const [fetchedValues, { updateAttributeTableFetchedValue }] = useFetchedValues();
    const entityId = usePropsStoreValue("entityId");
    const [draggedAttributeId, setDraggedAttributeId] = useState(null);
    const [{ isAttributeFormBuilderChanged }, setChangeBoolean] = useChangeBooleans();

    const toolbarAttributes = useMemo(() => {
        if (fetchedValues && fetchedValues.attributeTable && fetchedValues.attributeTable.attributes) {
            const attributes = filterAttributesForComponent(
                fetchedValues.attributeTable.attributes,
                selectedComponent.componentType,
                selectedPage.subPageType
            );
            const compponentAttributes = []
            for (const step of steps) {
                const attributes = getAttributesFromLayout(
                    step.layout,
                    fetchedValues.attributeTable.attributes,
                    entityId
                );
                compponentAttributes.push(...attributes);
            }
            const selectableAttributes = attributes.filter(attribute => !compponentAttributes.some(attr => attr.attributeId === attribute._id));
            const sortedSelectableAttributes = selectableAttributes.sort((a, b) => a.order - b.order).sort(bySystemAttributeDesc);
            const normalAttributes = sortedSelectableAttributes.filter(attribute => attribute.isSystemAttribute === false || attribute.isSystemAttribute === undefined);
            const systemAttributes = sortedSelectableAttributes.filter(attribute => attribute.isSystemAttribute);
            const sortAlphabetically = normalAttributes.sort((a, b) => a.attributeLabel.localeCompare(b.attributeLabel));
            return [...sortAlphabetically, ...systemAttributes];;
        }
        return [];
    }, [fetchedValues, steps])

    useEffect(() => {
        getEntityAttributes();
    }, [selectedComponent]);

    useEffect(() => {
        getAttributeFromBuilderLayout();
    }, []);

    useEffect(() => {
        const isAnyStepChanged = isStepsChanged(steps, selectedComponent.steps);
        const isAnyActionsChanged = isFormActionsChanged(formActions, selectedComponent.formActions);
        const isChanged = isAnyStepChanged || isAnyActionsChanged;
        setChangeBoolean("isAttributeFormBuilderChanged", isChanged);
    }, [steps, formActions])

    async function getEntityAttributes() {
        if (state.loading) {
            const attributes = await getAttributes(entityId);
            updateAttributeTableFetchedValue("attributes", attributes);
            const referenceAttributePages = await getReferenceAttributePages(attributes);
            updateAttributeTableFetchedValue("referenceAttributePages", referenceAttributePages);
            const attributesTableData = attributes.map(attribute => ({
                ...attribute,
                attributeId: attribute._id,
                entityId: entityId
            }));
            dispatch.setAttributes(attributesTableData);
        }
    }

    function getAttributeFromBuilderLayout() {
        if (formBuilderLoading) {
            dispatchLayout.setSteps(selectedComponent.steps);
            dispatchLayout.setSelectedStep(selectedComponent.steps[0].id);
            dispatchLayout.setFormActions(selectedComponent.formActions);
        }
    }

    async function getReferenceAttributePages(attributes) {
        const allPages = [];
        for (const attribute of attributes) {
            attribute.entityId = entityId;
            if (attribute.attributeType === "Entity Lookup") {
                const { data: pages } = await getEntityPages(attribute.entityId, selectedPage.portalId);
                const singleAndEditRecordPages = pages.filter(page => page.subPageType === "Single Record" || page.subPageType === "Edit Record");
                allPages.push(...singleAndEditRecordPages);
            }
        }
        return allPages;
    }

    function handleRecommendation(sections) {
        const { attributes } = fetchedValues.attributeTable;
        for (const section of sections) {
            section.columns = section.columns.filter(column => (
                attributes.some(attribute => attribute._id === column.attributeId)
            ))
            for (const column of section.columns) {
                const attribute = attributes.find(attribute => attribute._id === column.attributeId);
                column.i = uuidv4();
                column.attributeType = attribute.attributeType;
                column.cellRules = attribute.showCellFormatingIfStatement && selectedComponent.componentType === "View";
                column.maxW = 12;
                switch (attribute.attributeType) {
                    case "Multiline Text":
                        column.minH = 2;
                        column.minW = 6;
                        column.h = 2;
                        break;
                    case "Html":
                        column.minH = 3;
                        column.minW = 6;
                        column.h = 3;
                        break;
                    default:
                        column.minH = 1;
                        column.minW = 1;
                        column.h = 1;
                        break;
                }
            }
        }
        dispatch.updateLayoutFromRecommendation(sections);
    }

    if (state.loading) {
        return (
            <Skeleton
                rows={[
                    { columns: [{ size: 12 }] },
                    { full: true }
                ]} />
        )
    }

    return (
        <div className="container-fluid h-100">
            {state.showSyncAttributeDialog && <SyncAttributeDialog componentTypes={["Table", "Cards"]} />}
            <ComponentAttributeToolbar
                onRecommend={handleRecommendation}
                showSelectAttributeButton={false}
                showOptimizeAIButton={true}
                isOptimizeButtonDisabled={steps.length > 1}
            />
            <div className="row form-builder-container">
                <div className="col-2 form-builder-toolbar">
                    <Scrollable>
                        <Toolbar attributes={toolbarAttributes} onDragAttribute={setDraggedAttributeId} />
                    </Scrollable>
                </div>
                <div className="col-10 form-builder d-flex flex-column">
                    {properties.isMultiStepForm ?
                        <Steps draggedAttributeId={draggedAttributeId} onDropAttribute={setDraggedAttributeId} />
                        :
                        <div className="form-builder-step-layout">
                            <CreateSection />
                            <StepLayout draggedAttributeId={draggedAttributeId} onDropAttribute={setDraggedAttributeId} />
                        </div>
                    }
                    <FormActions step={steps.findIndex(step => step.id === selectedStepId)} />
                </div>
            </div>
        </div>
    );
};

export default AttributeFormBuilder;